import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Popconfirm } from 'antd';
import { UserOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import AdminPageLayout from './layout/AdminPageLayout';
import useEmployeeStore from '../../stores/employeeStore';

const Employee = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { employees, loading, fetchEmployees, createEmployee, updateEmployee, deleteEmployee } = useEmployeeStore();

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const showModal = (employee = null) => {
    setEditingEmployee(employee);
    if (employee) {
      form.setFieldsValue(employee);
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      filters: [
        { text: 'Manager', value: 'Manager' },
        { text: 'Cleaner', value: 'Cleaner' },
        { text: 'Assistant', value: 'Assistant' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => showModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete Employee"
            description="Are you sure?"
            onConfirm={() => deleteEmployee(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AdminPageLayout
      title="Employees"
      icon={<UserOutlined />}
      subtitle="Manage your employees"
      actions={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
          Add Employee
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={employees}
        loading={loading}
        rowKey="id"
      />

      <Modal
        title={editingEmployee ? "Edit Employee" : "Add Employee"}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingEmployee(null);
        }}
        confirmLoading={submitting}
        okText={submitting ? "Saving..." : "Save"}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            setSubmitting(true);
            try {
              const result = editingEmployee 
                ? await updateEmployee(editingEmployee.id, values)
                : await createEmployee(values);
              
              if (result.success) {
                setIsModalVisible(false);
                form.resetFields();
                setEditingEmployee(null);
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="Manager">Manager</Select.Option>
              <Select.Option value="Cleaner">Cleaner</Select.Option>
              <Select.Option value="Assistant">Assistant</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true },
              { type: 'email' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </AdminPageLayout>
  );
};

export default Employee;
